import { useTranslation } from 'react-i18next';
import { Field, FieldLabel } from '../../ui/field';
import { formulationKey } from './formulationSearch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { PrescribableMedication } from './types';

export interface FormulationPickerProps {
  formulations: PrescribableMedication[];
  /** The selected formulation's {@link formulationKey}. */
  selectedCode: string;
  onFormulationChange: (code: string) => void;
  isLoading: boolean;
  /** Render the field greyed out (e.g. before a medication is chosen, when there is nothing to pick). */
  disabled?: boolean;
}

/** Step 2 of the medication picker: choose a dispensable strength/form. Directions are entered in
 * the ScriptSure signing iframe rather than collected here (#5991). */
export function FormulationPicker({
  formulations,
  selectedCode,
  onFormulationChange,
  isLoading,
  disabled = false,
}: FormulationPickerProps): React.ReactNode {
  const { t } = useTranslation('records');
  return (
    <Field>
      <FieldLabel htmlFor="med-formulation">{t('strengthAndForm')}</FieldLabel>
      <Select value={selectedCode} onValueChange={onFormulationChange} disabled={disabled || isLoading}>
        <SelectTrigger id="med-formulation" aria-label={t('strengthAndForm')}>
          {/* Empty placeholder before a medication is chosen (disabled) — "no strengths found" only
              applies once a drug is searched. */}
          <SelectValue placeholder={isLoading ? t('loading') : disabled ? '' : t('noStrengthsFound')} />
        </SelectTrigger>
        <SelectContent>
          {formulations.map((m) => (
            <SelectItem key={formulationKey(m)} value={formulationKey(m)}>
              {m.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
